<template>
  <div class="monthly-revenue-chart">
    <div class="filters">
      <label>Tipo de Licitação:</label>
      <select
        v-model="filters.biddingTypeSelecionado"
        class="filter-select"
        @change="updateChart"
      >
        <option
          value=""
        >
          Todos
        </option>
        <option
          v-for="type in biddingTypes"
          :key="type"
          :value="type"
        >
          {{ type }}
        </option>
      </select>

      <label class="ml-3">Ano:</label>
      <select
        v-model="filters.anoSelecionado"
        class="filter-select"
        @change="updateChart"
      >
        <option
          v-for="year in availableYears"
          :key="year"
          :value="year"
        >
          {{ year }}
        </option>
      </select>

      <label class="ml-3">Estado:</label>
      <select
        v-model="filters.estado"
        class="filter-select"
        @change="updateChart"
      >
        <option value="">
          Todos
        </option>
        <option
          v-for="estado in estadosDisponiveis"
          :key="estado"
          :value="estado"
        >
          {{ estado }}
        </option>
      </select>
    </div>

    <bar-chart
      :chart-data="chartData"
      :options="chartOptions"
    />
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs'
import { reactiveProp } from 'vue-chartjs/es/mixins'
import fetchReportData from '@/syslic/dashboard/reportService'
import filterMonthlyRevenueData from '@/syslic/dashboard/revenue/filterMonthlyRevenueData'

export default {
  name: 'MonthlyRevenueChart',
  components: {
    barChart: {
      extends: Bar,
      mixins: [reactiveProp],
      props: ['chartData', 'options'],
      mounted() {
        this.renderChart(this.chartData, this.options)
      },
      watch: {
        chartData(newData) {
          this.renderChart(newData, this.options)
        },
      },
    },
  },
  data() {
    const currentYear = new Date().getFullYear()
    return {
      rawData: [],
      chartData: { labels: [], datasets: [] },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [
            {
              id: 'y-receita',
              position: 'left',
              ticks: { beginAtZero: true },
              scaleLabel: {
                display: true,
                labelString: 'R$',
              },
            },
            {
              id: 'y-porcentagem',
              position: 'right',
              ticks: {
                beginAtZero: true,
                max: 100,
              },
              scaleLabel: {
                display: true,
                labelString: 'Variação (%)',
              },
              gridLines: {
                drawOnChartArea: false,
              },
            },
          ],
          xAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: 'Período',
              },
            },
          ],
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label(tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex]
              const val = dataset.data[tooltipItem.index]
              return `${dataset.label}: ${
                typeof val === 'number' ? val.toFixed(2) : val
              }`
            },
          },
        },
        legend: { position: 'top' },
      },
      filters: {
        biddingTypeSelecionado: '',
        estado: '',
        anoSelecionado: currentYear,
      },
      availableYears: Array.from({ length: 5 }, (_, i) => currentYear - i),
      biddingTypes: [
        'Aquisição',
        'Aviso de Contratação Direta',
        'Edital',
        'SRP',
      ],
    }
  },
  computed: {
    estadosDisponiveis() {
      const estadosSet = new Set()
      this.rawData.forEach(item => {
        if (item.stateName) {
          estadosSet.add(item.stateName)
        }
      })
      return Array.from(estadosSet).sort()
    },
  },
  watch: {
    'filters.biddingTypeSelecionado': 'updateChart',
    'filters.estado': 'updateChart',
    'filters.anoSelecionado': 'updateChart',
  },
  async mounted() {
    await this.loadData()
  },
  methods: {
    async loadData() {
      this.rawData = await fetchReportData(this, this.filters)
      this.updateChart()
    },
    updateChart() {
      const processed = filterMonthlyRevenueData(this.rawData, this.filters)
      this.chartData = {
        labels: processed.labels,
        datasets: processed.datasets,
      }
    },
  },
}
</script>

<style scoped>
.filters {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  align-items: center;
}
.filter-select {
  padding: 0.5rem;
  font-size: 1rem;
  border-radius: 0.25rem;
  border: 1px solid #ccc;
  max-width: 150px;
  width: 100%;
}

.ml-3 {
  margin-left: 1rem;
}

.chart-box {
  width: 100%;
  height: 400px;
}
</style>
